import { NavLink, useLocation } from 'react-router-dom';
import { LayoutDashboard, Users, Calendar, UserCog, MoreHorizontal, Plus } from 'lucide-react';
import { cn } from '@lib/utils';

/**
 * Item de navegación inferior
 */
interface NavItem {
  path: string;
  icon: typeof LayoutDashboard;
  label: string;
  badge?: number;
}

/**
 * Barra de navegación inferior para móvil
 * 
 * Muestra los 5 ítems principales de navegación con iconos y labels.
 * Solo visible en pantallas < 1024px.
 * 
 * @example
 * ```tsx
 * <BottomNavigation />
 * ```
 */
export function BottomNavigation() {
  const location = useLocation();

  const navItems: NavItem[] = [
    {
      path: '/admin/dashboard',
      icon: LayoutDashboard,
      label: 'Inicio',
    },
    {
      path: '/admin/clients',
      icon: Users,
      label: 'Clientes',
    },
    {
      path: '/admin/citas',
      icon: Calendar,
      label: 'Citas',
    },
    {
      path: '/admin/tecnicos',
      icon: UserCog,
      label: 'Técnicos',
    },
    {
      path: '/admin/more',
      icon: MoreHorizontal,
      label: 'Más',
    },
  ];

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-40 border-t border-gray-200 bg-white lg:hidden"
      role="navigation"
      aria-label="Navegación principal móvil"
      style={{
        paddingBottom: 'env(safe-area-inset-bottom)', // Safe area para iOS notch
      }}
    >
      <div className="flex items-center justify-around">
        {navItems.map((item, index) => {
          const Icon = item.icon;
          const isActive = location.pathname.startsWith(item.path);
          
          // El ítem central (Citas) es especial
          const isCenterItem = index === 2;

          if (isCenterItem) {
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className="relative flex min-w-0 flex-1 flex-col items-center justify-center gap-1 py-2"
              >
                {/* FAB mini integrado */}
                <div
                  className={cn(
                    'flex h-12 w-12 items-center justify-center rounded-full transition-all',
                    isActive
                      ? 'bg-merino-500 text-white shadow-lg scale-110'
                      : 'bg-merino-100 text-merino-600'
                  )}
                >
                  <Plus className="h-5 w-5" />
                </div>
                <span
                  className={cn(
                    'text-[10px] font-medium transition-colors',
                    isActive ? 'text-merino-600' : 'text-gray-600'
                  )}
                >
                  {item.label}
                </span>
              </NavLink>
            );
          }

          return (
            <NavLink
              key={item.path}
              to={item.path}
              className="relative flex min-w-0 flex-1 flex-col items-center justify-center gap-1 py-3 transition-all active:scale-95"
            >
              {({ isActive: active }) => (
                <>
                  <div
                    className={cn(
                      'flex h-6 w-6 items-center justify-center transition-all',
                      active && 'scale-110'
                    )}
                  >
                    <Icon
                      className={cn(
                        'h-6 w-6 transition-colors',
                        active ? 'text-merino-600' : 'text-gray-500'
                      )}
                    />
                  </div>
                  
                  <span
                    className={cn(
                      'text-[10px] font-medium transition-colors',
                      active ? 'text-merino-600' : 'text-gray-600'
                    )}
                  >
                    {item.label}
                  </span>

                  {/* Badge opcional */}
                  {item.badge && item.badge > 0 && (
                    <span className="absolute right-4 top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-danger-500 px-1 text-[9px] font-bold text-white">
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}

                  {/* Indicador activo */}
                  {active && (
                    <span className="absolute bottom-0 left-1/2 h-1 w-8 -translate-x-1/2 rounded-t-full bg-merino-500" />
                  )}
                </>
              )}
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}
